import { useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Badge, badgeVariants } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { PartyPopper, RotateCcw } from 'lucide-react'
import { DateTime } from 'luxon'
import { DETAILS_SYNC_TIMEOUT } from '../../constants'
import { useDebouncedEdit, useIsMobile } from '../../hooks'
import useIsReadOnly from '../../hooks/useIsReadOnly'
import {
    useGetGoalRecurringTemplateLinks,
    useGetGoalReview,
    useLinkRecurringTemplateToGoals,
} from '../../services/api/goals.hooks'
import { useRecurringTaskTemplates } from '../../services/api/recurring-tasks.hooks'
import { useGetTasksV4, useModifyTask } from '../../services/api/tasks.hooks'
import { icons } from '../../styles/images'
import { TRecurringTaskTemplate, TTaskV4 } from '../../utils/types'
import { stopKeydownPropogation } from '../../utils/utils'
import DueDate from '../atoms/DueDate'
import GTTextField from '../atoms/GTTextField'
import { Icon } from '../atoms/Icon'
import GTButton from '../atoms/buttons/GTButton'
import MarkTaskDoneButton from '../atoms/buttons/MarkTaskDoneButton'
import CreateNewItemInput from '../molecules/CreateNewItemInput'
import GTDatePicker from '../molecules/GTDatePicker'
import GTDropdownMenu from '../radix/GTDropdownMenu'
import { GTMenuItem } from '../radix/RadixUIConstants'
import Tip from '../radix/Tip'
import DetailsViewTemplate from '../templates/DetailsViewTemplate'
import GoalPlanDialogs from './GoalPlanDialogs'
import { GoalStatusBadge } from './GoalsList'
import { activeRecurringTemplates, addGoalToTemplateHook, removeGoalFromTemplateHook } from './RecurringGoalHooks'
import Repeats from './Repeats'
import SectionLabel from './SectionLabel'
import WeekHistory from './WeekHistory'
import { useGoalCreation } from './creation/shared/GoalCreationContext'
import { goalReadStrip, reviewRecentTitle, weekStepTaskId } from './goalReviewRules'
import { goalIdForTask, isDueToday, linkedTasksForGoal, nextUpOrder } from './goalTasks'
import {
    GOAL_PAUSE_PATCH,
    GOAL_RESUME_PATCH,
    GOAL_SELF_GRADES,
    GOAL_STATUS_LABEL,
    TGoal,
    TGoalContribution,
    TGoalPatch,
    TGoalPlanCadence,
    TGoalUnitSource,
    accomplishedDateLabel,
    accomplishedReadout,
    formatGoalAmount,
    goalAttributionTail,
    goalHint,
    goalStepUpPatch,
    goalStepUpTarget,
    goalUnitSource,
    isGoalAchieved,
    isGoalArchived,
    isGoalClosed,
    isLegacyFinishedGoal,
    shouldOfferGoalStepUp,
} from './goalTypes'

/** The due-date sentinel the task API reads as "no due date". */
const CLEAR_DUE_DATE = '1969-12-31'
const RECENT_LIMIT = 5
/** The source tag of a Recent row drawn from a weekly review rather than a saved contribution. */
const REVIEW_SOURCE = 'Review'
const TITLE_MAX_HEIGHT = 208

// Timeframe and target are free-form saved values. Keep every chip atomic while capping those two so
// one old or unusually long label cannot widen the details pane; immutable chips can reveal the full value.
const CHIP_CLASS =
    'box-border h-7 max-w-60 min-w-0 shrink-0 gap-1.5 overflow-hidden whitespace-nowrap rounded-gt-md border border-border bg-card text-body-md font-medium text-secondary-foreground'
const CHIP_VALUE_CLASS = 'min-w-0 truncate'
/** The same chip, as a control: hover and focus are what say it can be changed. */
const CHIP_BUTTON_CLASS = cn(
    badgeVariants({ variant: 'secondary' }),
    CHIP_CLASS,
    'transition-colors hover:bg-muted focus-visible:ring-1 focus-visible:ring-ring active:scale-[0.98]'
)

/** The cadence rows accepted with the plan. These are intentionally separate from Repeats: only a
 * linked recurring template generates tasks and earns progress. */
const PlanCadences = ({ cadences }: { cadences: TGoalPlanCadence[] }) => (
    <section className="space-y-1.5" aria-label="Cadences">
        <SectionLabel>Cadences</SectionLabel>
        <div className="flex flex-col">
            {cadences.map((cadence, index) => (
                <div
                    key={`${cadence.title}-${cadence.frequencyLabel}-${index}`}
                    className="flex items-center gap-3 border-b border-hairline py-2 last:border-b-0"
                >
                    <Icon icon={icons.list} color="gray" size="small" />
                    <span className="min-w-0 flex-1 truncate text-body-sm text-foreground">{cadence.title}</span>
                    {cadence.frequencyLabel && (
                        <span className="shrink-0 text-label-md text-muted-foreground">{cadence.frequencyLabel}</span>
                    )}
                </div>
            ))}
        </div>
    </section>
)

/** Same idiom as TaskDetails: one horizontal row of property chips, the label becomes the tooltip. */
const PropertyChip = ({
    label,
    icon,
    value,
    revealValue,
    children,
}: {
    label: string
    icon?: React.ReactNode
    value?: string
    revealValue?: boolean
    children: React.ReactNode
}) => {
    if (revealValue) {
        return (
            <Popover>
                <Tip content={label} fitContent>
                    <PopoverTrigger
                        className={CHIP_BUTTON_CLASS}
                        aria-label={`Show full ${label.toLowerCase()}`}
                        data-goal-property-label={label}
                    >
                        {icon}
                        <span className={CHIP_VALUE_CLASS} data-goal-property-value>
                            {children}
                        </span>
                    </PopoverTrigger>
                </Tip>
                <PopoverContent align="start" className="w-60 max-w-[calc(100vw-2rem)] p-3">
                    <p className="text-label-md text-muted-foreground">{label}</p>
                    <p
                        className="mt-1 break-words text-body-sm text-foreground"
                        data-testid="goal-property-full-value"
                        data-goal-property-label={label}
                    >
                        {children}
                    </p>
                </PopoverContent>
            </Popover>
        )
    }

    return (
        <Tip content={label} fitContent>
            <Badge variant="secondary" className={CHIP_CLASS} title={value} data-goal-property-label={label}>
                {icon}
                <span className={CHIP_VALUE_CLASS} data-goal-property-value>
                    {children}
                </span>
            </Badge>
        </Tip>
    )
}

/**
 * A property chip that opens a one-field popover — the free-text counterpart to the dropdowns in a
 * task's property row. It commits on Enter or when the popover closes, and Escape closes without
 * saving. It deliberately does not save per keystroke the way the title does: a weekly target
 * passes through "1" on the way to "12", and each pass would re-pace the goal.
 */
const EditableChip = ({
    label,
    icon,
    value,
    onCommit,
    children,
    inputProps,
    disabled,
}: {
    label: string
    icon?: React.ReactNode
    value: string
    onCommit: (next: string) => void
    children: React.ReactNode
    inputProps?: React.ComponentProps<typeof Input>
    disabled?: boolean
}) => {
    const [open, setOpen] = useState(false)
    const [draft, setDraft] = useState(value)
    const cancelled = useRef(false)

    const onOpenChange = (next: boolean) => {
        if (next) {
            setDraft(value)
            cancelled.current = false
        } else if (!cancelled.current && draft.trim() !== value) {
            onCommit(draft.trim())
        }
        setOpen(next)
    }

    if (disabled) {
        return (
            <PropertyChip label={label} icon={icon} value={value} revealValue>
                {children}
            </PropertyChip>
        )
    }

    return (
        <Popover open={open} onOpenChange={onOpenChange}>
            <Tip content={label} fitContent>
                <PopoverTrigger
                    className={CHIP_BUTTON_CLASS}
                    aria-label={label}
                    title={value || undefined}
                    data-goal-property-label={label}
                >
                    {icon}
                    <span className={CHIP_VALUE_CLASS} data-goal-property-value>
                        {children}
                    </span>
                </PopoverTrigger>
            </Tip>
            <PopoverContent align="start" className="w-56 p-2" onEscapeKeyDown={() => (cancelled.current = true)}>
                <Input
                    autoFocus
                    aria-label={label}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key !== 'Enter') return
                        e.preventDefault()
                        onOpenChange(false)
                    }}
                    className="h-8"
                    {...inputProps}
                />
            </PopoverContent>
        </Popover>
    )
}

/**
 * The end date, on the same calendar tasks use for due dates. GTDatePicker reports a pick as a
 * full ISO timestamp and a clear as the epoch; updateGoal converts both (normalizeGoalEndDate),
 * so this only has to say what happened.
 */
const EndDateChip = ({
    endDate,
    onChange,
    disabled,
}: {
    endDate?: string
    onChange: (next: string) => void
    disabled?: boolean
}) => {
    const [open, setOpen] = useState(false)
    const date = endDate ? DateTime.fromISO(endDate) : null
    const weeksLeft = date?.isValid ? Math.max(0, Math.ceil(date.diff(DateTime.local(), 'weeks').weeks)) : null
    const label = date?.isValid
        ? `${date.toFormat('MMM d')} · ${weeksLeft} wk${weeksLeft === 1 ? '' : 's'} left`
        : 'Open-ended'

    if (disabled) {
        return (
            <PropertyChip
                label="End date"
                icon={<Icon icon={icons.calendar_blank} color="gray" size="small" />}
                value={label}
            >
                {label}
            </PropertyChip>
        )
    }

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <Tip content="End date" fitContent>
                <PopoverTrigger
                    className={CHIP_BUTTON_CLASS}
                    aria-label="End date"
                    title={label}
                    data-goal-property-label="End date"
                >
                    <Icon icon={icons.calendar_blank} color="gray" size="small" />
                    <span className={CHIP_VALUE_CLASS} data-goal-property-value>
                        {label}
                    </span>
                </PopoverTrigger>
            </Tip>
            <PopoverContent align="start" className="w-auto p-0">
                <GTDatePicker
                    onlyCalendar
                    initialDate={DateTime.fromISO(endDate ?? '')}
                    setDate={(next) => {
                        onChange(next)
                        setOpen(false)
                    }}
                />
            </PopoverContent>
        </Popover>
    )
}

/**
 * A self-graded goal's verdict is its owner's call, and the Log progress dialog only takes it
 * bundled with an activity entry — so setting it there means filing a log you did not do. The badge
 * is where the status is read, so for those goals it is also where it is set. Every other goal's
 * status is derived from its contributions and the badge stays a read-only badge.
 */
const StatusControl = ({ goal, onChange }: { goal: TGoal; onChange: (patch: TGoalPatch) => void }) => {
    const [open, setOpen] = useState(false)
    if (goal.progressMode !== 'manual') {
        return (
            <Tip content="Status" fitContent>
                <span className="flex self-start">
                    <GoalStatusBadge status={goal.status} />
                </span>
            </Tip>
        )
    }
    return (
        <Popover open={open} onOpenChange={setOpen}>
            <Tip content="Set status" fitContent>
                <PopoverTrigger
                    aria-label="Status"
                    className="flex self-start rounded-full focus-visible:ring-1 focus-visible:ring-ring"
                >
                    <GoalStatusBadge status={goal.status} />
                </PopoverTrigger>
            </Tip>
            <PopoverContent align="start" className="w-40 p-1">
                {GOAL_SELF_GRADES.map((status) => (
                    <button
                        key={status}
                        type="button"
                        onClick={() => {
                            onChange({ status })
                            setOpen(false)
                        }}
                        className={cn(
                            'flex w-full items-center rounded-md px-2 py-1.5 text-left text-body-sm text-foreground transition-colors hover:bg-muted',
                            goal.status === status && 'bg-secondary'
                        )}
                    >
                        {GOAL_STATUS_LABEL[status]}
                    </button>
                ))}
            </PopoverContent>
        </Popover>
    )
}

/** Which of the four visual states a week is in; "current" is an outline layered on top, not a state. */
type TUnlinkedCreatedTaskLink = { taskId: string; goalId: string; ownerGoalId: string }

/** What the client calls a by-hand entry: the API requires a title and this flow deliberately asks for none. */
const MANUAL_TITLE = 'Added by hand'
const DEFAULT_MANUAL_HOURS = '0.5'
const HOUR_SHORTCUTS = ['0.5', '1', '2']

/**
 * One glyph per source, because the meter is the explanation: a week that reads [+][+][ ] is
 * self-evidently manual without a word of prose saying so.
 */
const UNIT_GLYPH: Record<TGoalUnitSource, string> = { task: '✓', recurring: '↻', manual: '+' }
const UNIT_SOURCE_LABEL: Record<TGoalUnitSource, string> = {
    task: 'from a task',
    recurring: 'from a recurring task',
    manual: 'added by hand',
}

const relativeWhen = (contribution: TGoalContribution) => {
    const at = contribution.at ? DateTime.fromISO(contribution.at) : null
    return at?.isValid ? at.toRelativeCalendar() ?? '' : contribution.date ?? ''
}

/**
 * Adding one by hand, for the two shapes it takes. Sessions arm in place — there is nothing to fill
 * in, so a dialog would only be ceremony — while hours open a small popover, because they carry a
 * value. Both report the amount they are about to write so the meter can preview it.
 */
const useByHandAdd = (goal: TGoal) => {
    const { logGoalProgress } = useGoalCreation()
    const [armed, setArmed] = useState(false)
    const [hours, setHours] = useState(DEFAULT_MANUAL_HOURS)
    const isHours = goal.unit === 'hrs'
    const parsedHours = Number(hours)
    const validHours = Number.isFinite(parsedHours) && parsedHours > 0
    const pending = !armed ? 0 : isHours ? (validHours ? parsedHours : 0) : 1

    const disarm = () => {
        setArmed(false)
        setHours(DEFAULT_MANUAL_HOURS)
    }
    const commit = () => {
        if (pending <= 0) return
        logGoalProgress(goal.id, { title: MANUAL_TITLE, amount: pending })
        disarm()
    }
    return { armed, setArmed, hours, setHours, isHours, validHours, pending, commit, disarm }
}

type TByHandAdd = ReturnType<typeof useByHandAdd>

/** The text link that arms a by-hand add, and — for hours — the one popover that takes the number. */
const ByHandControl = ({ add, disabled }: { add: TByHandAdd; disabled?: boolean }) => {
    // The hours popover drives the same `armed` flag through onOpenChange, so the trigger must not
    // also arm on click — it would re-open the popover the same click just closed.
    const link = (onClick?: () => void) => (
        <button
            type="button"
            disabled={disabled}
            onClick={onClick}
            className="h-6 shrink-0 rounded-md px-1.5 text-label-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-50"
        >
            + by hand
        </button>
    )
    if (!add.isHours) {
        if (!add.armed) return link(() => add.setArmed(true))
        return (
            <span className="flex shrink-0 items-center gap-1">
                <Button size="sm" className="h-6 px-2 active:scale-[0.96]" onClick={add.commit}>
                    Count it
                </Button>
                <button
                    type="button"
                    onClick={add.disarm}
                    className="h-6 rounded-md px-1.5 text-label-md text-muted-foreground transition-colors hover:bg-muted"
                >
                    Cancel
                </button>
            </span>
        )
    }
    return (
        <Popover open={add.armed} onOpenChange={(next) => (next ? add.setArmed(true) : add.disarm())}>
            <PopoverTrigger asChild disabled={disabled}>
                {link()}
            </PopoverTrigger>
            <PopoverContent align="end" className="w-52 p-2">
                <div className="flex items-center gap-1.5">
                    <Input
                        autoFocus
                        type="number"
                        min={0.5}
                        step={0.5}
                        value={add.hours}
                        onChange={(e) => add.setHours(e.target.value)}
                        onKeyDown={(e) => {
                            stopKeydownPropogation(e, ['Escape'])
                            if (e.key !== 'Enter') return
                            e.preventDefault()
                            add.commit()
                        }}
                        className="h-8 w-20"
                        aria-label="Hours to add"
                    />
                    {HOUR_SHORTCUTS.map((h) => (
                        <button
                            key={h}
                            type="button"
                            onClick={() => add.setHours(h)}
                            className={cn(
                                'h-8 flex-1 rounded-md border border-border text-label-md text-secondary-foreground transition-colors hover:bg-muted',
                                add.hours === h && 'border-foreground bg-secondary text-foreground'
                            )}
                        >
                            {h}
                        </button>
                    ))}
                </div>
                <Button
                    size="sm"
                    className="mt-2 w-full active:scale-[0.96]"
                    disabled={!add.validHours}
                    onClick={add.commit}
                >
                    Add {add.validHours ? formatGoalAmount(add.pending) : '—'} hrs
                </Button>
            </PopoverContent>
        </Popover>
    )
}

/**
 * A goal with no weekly pace has no meter to arm against, but it still takes entries — so it gets
 * the same control without the preview, rather than being left with the dialog nothing else uses.
 */
const UnpacedByHand = ({ goal }: { goal: TGoal }) => {
    const add = useByHandAdd(goal)
    return <ByHandControl add={add} disabled={goal.status === 'paused'} />
}

/**
 * One filled or empty unit of the meter. A filled one names what put it there on hover and focus.
 * The flex sizing lives on the wrapper, not the segment: Tip renders a span around its child, so a
 * flex-1 on the segment itself would size it inside that span and leave labelled units narrower
 * than plain ones.
 */
const UnitSegment = ({
    contribution,
    filled,
    ghost,
}: {
    contribution?: TGoalContribution | null
    filled?: boolean
    ghost?: boolean
}) => {
    const base = 'flex h-[15px] w-full items-center justify-center rounded-sm text-[9px] leading-none'
    if (ghost) {
        return (
            <div
                aria-hidden
                className={cn(
                    base,
                    'border border-dashed border-foreground/40 text-foreground/40 animate-in fade-in-0'
                )}
            >
                +
            </div>
        )
    }
    if (!contribution) {
        return <div className={cn(base, 'transition-colors duration-300', filled ? 'bg-foreground' : 'bg-border')} />
    }
    const source = goalUnitSource(contribution)
    const when = relativeWhen(contribution)
    const label = `${contribution.title}${when ? ` · ${when}` : ''} · ${UNIT_SOURCE_LABEL[source]}`
    return (
        <Tip content={label}>
            <button
                type="button"
                aria-label={label}
                className={cn(
                    base,
                    'bg-foreground text-background transition-colors duration-300 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-1'
                )}
            >
                {UNIT_GLYPH[source]}
            </button>
        </Tip>
    )
}

/**
 * Hours cannot carry one glyph per unit, so the bar splits by source instead and leans on the
 * attribution tail for the same read. The pending add is hatched, matching the ghost unit.
 */
const SplitBar = ({
    units,
    done,
    target,
    pending,
}: {
    units: TGoalContribution[]
    done: number
    target: number
    pending: number
}) => {
    const width = (amount: number) => `${Math.min(100, (amount / Math.max(target, 1)) * 100)}%`
    const fromTasks = units.filter((u) => goalUnitSource(u) !== 'manual').reduce((sum, u) => sum + (u.amount ?? 0), 0)
    return (
        <div className="flex h-[15px] flex-1 overflow-hidden rounded-sm bg-border">
            <div className="h-full bg-foreground transition-[width] duration-300" style={{ width: width(fromTasks) }} />
            <div
                className="h-full bg-foreground/50 transition-[width] duration-300"
                style={{ width: width(Math.max(0, done - fromTasks)) }}
            />
            {pending > 0 && (
                <div
                    aria-hidden
                    className="h-full border border-dashed border-foreground/40 bg-foreground/10"
                    style={{ width: width(pending) }}
                />
            )}
        </div>
    )
}

/**
 * The one framed block. Every filled unit says what put it there, and words appear in a single slot
 * below only when a state earns them — a healthy week carries no prose at all.
 */
export const ThisWeekBlock = ({
    goal,
    hasLinkedTasks,
    isArchived,
    onAttach,
    onNewTask,
    onRepair,
    repeatCount,
    recurringCreditGoalCount,
    review,
}: {
    goal: TGoal
    hasLinkedTasks: boolean
    isArchived: boolean
    onAttach?: () => void
    onNewTask?: () => void
    onRepair?: () => void
    repeatCount: number
    recurringCreditGoalCount: number
    /**
     * The weekly review's use of this card: the same count, glyphs and by-hand add, as evidence to
     * read rather than a surface to act from. It drops the hints that push toward linking or
     * repairing (the review asks its own question right below), and the cadence strip and contract
     * line, which GoalDetails still shows a click away.
     */
    review?: { label: string; since: string; notes: string[] }
}) => {
    const add = useByHandAdd(goal)
    const target = goal.weeklyTarget ?? 0
    const done = goal.thisWeek ?? 0
    const isHours = goal.unit === 'hrs'
    const isPaused = goal.status === 'paused'
    const units = goal.thisWeekUnits ?? []
    const pending = isPaused ? 0 : add.pending
    const projected = done + pending
    const isMet = done >= target
    const remaining = Math.max(0, target - done)
    const tail = goalAttributionTail(units, goal.unit)

    // One slot per unit of the target, or per unit logged if the week overshot. `thisWeek` is the
    // truth about how many are filled and the units only label them, so the two are tracked apart:
    // a slot the count includes but no contribution explains is filled and unlabelled, never empty.
    // A paused goal is exactly that case — it keeps its stored count, and recomputeGoal returns
    // before rebuilding the units it deliberately does not persist.
    const slots = useMemo(() => {
        if (isHours) return []
        const labels: (TGoalContribution | null)[] = []
        for (const unit of units) {
            for (let i = 0; i < Math.max(1, Math.round(unit.amount ?? 1)); i++) labels.push(unit)
        }
        const filledCount = Math.round(done)
        const total = Math.max(Math.round(target), filledCount, labels.length)
        return Array.from({ length: total }, (_, i) => ({
            filled: i < filledCount,
            contribution: i < filledCount ? labels[i] ?? null : null,
        }))
    }, [isHours, units, target, done])

    // The ghost takes the next empty slot rather than being appended, or an armed add on a week at
    // 1 of 3 would draw a fourth unit and read as overshooting a target it has not even met.
    const ghostIndex = pending > 0 && !isHours ? Math.round(done) : -1

    // A hooked recurring template feeds the goal just as a linked task does, so the first-run hint
    // must count it — otherwise a recurring-only goal is told nothing is linked while the backend
    // is already crediting its completions.
    const hasFeed = hasLinkedTasks || repeatCount > 0

    // Every hint here nudges toward feeding the goal, and an archived goal takes no new work — the
    // server refuses the link, so offering "New task" would create a real unfiled task from a flow
    // that looked like it was adding work to this goal.
    const hint =
        isArchived || review
            ? null
            : goalHint({
                  units,
                  hasFeed,
                  hasHistory: goal.recent.length > 0,
                  status: goal.status,
              })
    const lastCounted = goal.recent.find((c) => (c.amount ?? 0) > 0 && !!c.at)

    return (
        <div
            className={cn(
                'flex flex-col gap-3 rounded-gt-md border border-border bg-card p-3',
                isPaused && 'opacity-60'
            )}
        >
            <div className="flex items-baseline gap-2">
                <SectionLabel>{review ? review.label : 'This week'}</SectionLabel>
                <span className="ml-auto text-body-sm text-muted-foreground">
                    {review
                        ? review.since
                        : isPaused
                        ? 'Paused'
                        : isMet
                        ? 'Week met'
                        : `${formatGoalAmount(remaining)} more by Sun`}
                </span>
            </div>
            <div className="flex items-center gap-3">
                <span className="shrink-0 text-title-md tabular-nums text-foreground">
                    {formatGoalAmount(done)} of {formatGoalAmount(target)}
                    {isHours && ' hrs'}
                </span>
                {tail && <span className="shrink-0 text-body-sm text-muted-foreground">{tail}</span>}
                <div
                    role="progressbar"
                    aria-label="This week"
                    aria-valuemin={0}
                    aria-valuemax={target}
                    aria-valuenow={done}
                    aria-valuetext={goal.weekLabel}
                    className="flex min-w-0 flex-1 items-center gap-1"
                >
                    {isHours ? (
                        <SplitBar units={units} done={done} target={target} pending={pending} />
                    ) : (
                        <>
                            {slots.map((slot, i) => (
                                <span key={i} className="min-w-0 flex-1">
                                    <UnitSegment
                                        contribution={slot.contribution}
                                        filled={slot.filled}
                                        ghost={i === ghostIndex}
                                    />
                                </span>
                            ))}
                            {ghostIndex === slots.length && (
                                <span className="min-w-0 flex-1">
                                    <UnitSegment ghost />
                                </span>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* What feeds this goal, and the one way to move it by hand. Repeats are a count, not a
                list: the Repeats section below names them once, with their cadence and held time. */}
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-label-md text-muted-foreground">
                {review && done === 0 && pending === 0 && <span>Nothing recorded this week.</span>}
                {hasFeed && !review && (
                    <>
                        <span>Fed by</span>
                        {hasLinkedTasks && <span className="text-secondary-foreground">Linked tasks</span>}
                        {repeatCount > 0 && (
                            <span className="text-secondary-foreground">
                                {repeatCount} repeat{repeatCount === 1 ? '' : 's'}
                            </span>
                        )}
                    </>
                )}
                <span className="ml-auto flex items-center gap-1">
                    <ByHandControl add={add} disabled={isPaused || isArchived} />
                </span>
            </div>

            {/* Exactly one hint, and usually none. Always in the DOM so aria-live can announce into
                it, but reserving no height: a healthy week is the common case and should not pay
                for a blank line. */}
            <div aria-live="polite" className="text-body-sm text-muted-foreground">
                {pending > 0 ? (
                    <span>
                        would be {formatGoalAmount(projected)} of {formatGoalAmount(target)}
                        {isHours && ' hrs'}
                        {projected >= target && ' · week met'}
                    </span>
                ) : hint?.kind === 'nothing-linked' ? (
                    <span className="flex flex-wrap items-center gap-2">
                        Nothing linked yet. Goals move when you finish a linked task.
                        <Button size="sm" className="h-6 px-2 active:scale-[0.96]" onClick={onNewTask}>
                            New task
                        </Button>
                        <button
                            type="button"
                            onClick={onAttach}
                            className="h-6 rounded-md px-1.5 text-label-md transition-colors hover:bg-muted"
                        >
                            Attach
                        </button>
                    </span>
                ) : hint?.kind === 'quiet' ? (
                    <span className="flex flex-wrap items-center gap-2">
                        Nothing since {lastCounted ? relativeWhen(lastCounted) : 'last week'}.
                        <button
                            type="button"
                            onClick={onRepair}
                            className="h-6 rounded-md px-1.5 text-label-md transition-colors hover:bg-muted"
                        >
                            Repair
                        </button>
                    </span>
                ) : hint?.kind === 'all-by-hand' ? (
                    <span className="flex flex-wrap items-center gap-2">
                        All by hand this week.
                        <button
                            type="button"
                            onClick={onAttach}
                            className="h-6 rounded-md px-1.5 text-label-md transition-colors hover:bg-muted"
                        >
                            Link a task
                        </button>
                    </span>
                ) : null}
            </div>

            {review?.notes.map((note) => (
                <p key={note} className="text-body-sm text-muted-foreground">
                    {note}
                </p>
            ))}
            {!review && !!goal.weeks?.length && <WeekHistory key={goal.id} goal={goal} />}
            {!review && goal.contractLine && <p className="text-body-sm text-muted-foreground">{goal.contractLine}</p>}
            {!review && recurringCreditGoalCount > 1 && (
                <p className="text-body-sm text-muted-foreground">
                    Recurring completions credit {recurringCreditGoalCount} goals.
                </p>
            )}
        </div>
    )
}

const stepUpTargetLabel = (goal: Pick<TGoal, 'weeklyTarget' | 'unit'>): string => {
    const next = goalStepUpTarget(goal)
    return goal.unit === 'hrs' ? `${formatGoalAmount(next)} hrs / week` : `${formatGoalAmount(next)}× / week`
}

const StepUpOffer = ({
    goal,
    onAdd,
    onKeep,
    onDisable,
}: {
    goal: TGoal
    onAdd: () => void
    onKeep: () => void
    onDisable: () => void
}) => (
    <div className="flex flex-col gap-2 rounded-gt-md border border-gt-gold/40 bg-gt-gold/10 p-3 animate-in fade-in-0 slide-in-from-top-1">
        <div>
            <p className="text-body-sm font-medium text-foreground">
                This has held for two weeks. Want to add a little more?
            </p>
            <p className="mt-0.5 text-body-sm text-muted-foreground">Suggested increase: {stepUpTargetLabel(goal)}.</p>
        </div>
        <div className="flex flex-wrap gap-2">
            <Button size="sm" className="active:scale-[0.96]" onClick={onAdd}>
                Add a little more
            </Button>
            <Button variant="outline" size="sm" className="active:scale-[0.96]" onClick={onKeep}>
                Keep it here
            </Button>
            <Button variant="ghost" size="sm" className="text-muted-foreground active:scale-[0.96]" onClick={onDisable}>
                Not for this goal
            </Button>
        </div>
    </div>
)

/** A linked task in Next up: done toggle, title, due date, and the Today toggle (due date = today). */
const NextUpRow = ({
    task,
    onUnlink,
    readOnly,
    isWeekStep,
}: {
    task: TTaskV4
    onUnlink: () => void
    readOnly?: boolean
    /** The one step the owner picked in this week's review. */
    isWeekStep?: boolean
}) => {
    const { mutate: modifyTask } = useModifyTask()
    const inToday = isDueToday(task)
    return (
        <div className="group flex items-center gap-2.5 border-b border-hairline px-3 py-2 last:border-b-0">
            <MarkTaskDoneButton
                taskId={task.id}
                sectionId={task.id_folder}
                isDone={task.is_done}
                isSelected={false}
                isDisabled={readOnly || !!task.optimisticId}
                optimsticId={task.optimisticId}
            />
            <span
                className={cn(
                    'min-w-0 flex-1 truncate text-body-md',
                    task.is_done && 'text-muted-foreground line-through'
                )}
            >
                {task.title}
            </span>
            {isWeekStep && (
                <span className="shrink-0 rounded-sm bg-secondary px-1.5 py-0.5 text-label-sm text-secondary-foreground">
                    This week’s step
                </span>
            )}
            {!inToday && (
                <DueDate date={DateTime.fromISO(task.due_date)} isDoneOrDeleted={task.is_done || task.is_deleted} />
            )}
            {!readOnly && !task.is_done && (
                <button
                    type="button"
                    aria-pressed={inToday}
                    disabled={!!task.optimisticId}
                    onClick={() =>
                        modifyTask(
                            { id: task.id, dueDate: inToday ? CLEAR_DUE_DATE : DateTime.local().toISODate() },
                            task.optimisticId
                        )
                    }
                    className={cn(
                        'h-6 shrink-0 rounded-md px-2 text-label-md transition-[background-color,transform] duration-150 active:scale-[0.96]',
                        inToday
                            ? 'bg-action text-action-foreground hover:bg-action-hover'
                            : 'text-muted-foreground hover:bg-muted'
                    )}
                >
                    Today
                </button>
            )}
            {!readOnly && (
                <span className="opacity-0 transition-opacity group-hover:opacity-100">
                    <GTButton styleType="icon" icon={icons.x} tooltipText="Remove from goal" onClick={onUnlink} />
                </span>
            )}
        </div>
    )
}

/**
 * Attach an existing task: searchable list of open, unlinked tasks. Linear's "add existing
 * issues to a project", but one task at a time — this is a tiny surface.
 */
const AttachTaskPopover = ({
    candidates,
    onPick,
    open,
    setOpen,
}: {
    candidates: TTaskV4[]
    onPick: (task: TTaskV4) => void
    open: boolean
    setOpen: (open: boolean) => void
}) => {
    const [query, setQuery] = useState('')
    const q = query.trim().toLowerCase()
    const matches = candidates.filter((t) => !q || t.title.toLowerCase().includes(q)).slice(0, 8)
    return (
        <Popover
            open={open}
            onOpenChange={(next) => {
                setOpen(next)
                if (!next) setQuery('')
            }}
        >
            <PopoverTrigger asChild>
                <Button variant="ghost" size="sm" className="h-7 gap-1.5 px-2 text-muted-foreground">
                    <Icon icon={icons.link} color="gray" size="small" />
                    Attach existing
                </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-72 p-1">
                <Input
                    autoFocus
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search tasks…"
                    className="mb-1 h-8"
                />
                {matches.length === 0 ? (
                    <p className="px-2 py-2 text-body-sm text-muted-foreground">No open tasks match.</p>
                ) : (
                    matches.map((t) => (
                        <button
                            key={t.id}
                            type="button"
                            onClick={() => {
                                onPick(t)
                                setOpen(false)
                                setQuery('')
                            }}
                            className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-body-sm text-foreground transition-colors hover:bg-muted"
                        >
                            <span className="min-w-0 flex-1 truncate">{t.title}</span>
                            <DueDate date={DateTime.fromISO(t.due_date)} isDoneOrDeleted={false} />
                        </button>
                    ))
                )}
            </PopoverContent>
        </Popover>
    )
}

/**
 * Saved contributions only: one-off tasks, recurring hook tasks, manual logs, and legacy labels.
 * An entry worth nothing is a note and reads as one — outlined, not filled — so it is never
 * mistaken for a unit the meter counted. A note is always manual, so it outranks the other labels.
 */
const RecentRow = ({
    contribution,
    recurringGoalCount,
}: {
    contribution: TGoalContribution
    recurringGoalCount: number
}) => {
    // A weekly review is checked first: it is worth nothing too, but it is not a note.
    const isReview = contribution.source === REVIEW_SOURCE
    const isNote = (contribution.amount ?? 0) === 0
    const tag = isReview
        ? REVIEW_SOURCE
        : isNote
        ? 'Note'
        : contribution.source === 'Recurring'
        ? recurringGoalCount > 1
            ? `Recurring · ${recurringGoalCount} goals`
            : 'Recurring'
        : contribution.taskId
        ? 'Task'
        : contribution.source
    return (
        <div className="flex items-center gap-2 py-1.5">
            <span className="min-w-0 flex-1 truncate text-body-sm text-foreground">{contribution.title}</span>
            <span
                className={cn(
                    'shrink-0 rounded-sm px-1.5 text-label-sm text-muted-foreground',
                    isNote ? 'border border-border' : 'bg-muted'
                )}
            >
                {tag}
            </span>
            <span className="w-20 shrink-0 text-right text-body-sm capitalize text-muted-foreground">
                {relativeWhen(contribution)}
            </span>
        </div>
    )
}

/** A note records what happened without claiming the goal moved, so it is saved worth nothing. */
const NoteComposer = ({ goalId, onDone }: { goalId: string; onDone: () => void }) => {
    const { logGoalProgress } = useGoalCreation()
    const [text, setText] = useState('')
    const save = () => {
        if (!text.trim()) return
        logGoalProgress(goalId, { title: text.trim(), amount: 0 })
        onDone()
    }
    return (
        <div className="flex items-center gap-2 py-1.5">
            <Input
                autoFocus
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => {
                    // The composer sits inline in the pane, so without this a note containing "f"
                    // drops the app into Focus Mode mid-sentence.
                    stopKeydownPropogation(e, ['Escape'])
                    if (e.key === 'Escape') return onDone()
                    if (e.key !== 'Enter') return
                    e.preventDefault()
                    save()
                }}
                placeholder="What happened?"
                aria-label="Note"
                className="h-8"
            />
            <Button variant="outline" size="sm" className="shrink-0" disabled={!text.trim()} onClick={save}>
                Save
            </Button>
        </div>
    )
}

interface GoalDetailsProps {
    goal: TGoal
}
const GoalDetails = ({ goal }: GoalDetailsProps) => {
    const {
        setRepairGoalId,
        setArchiveGoalId,
        unarchiveGoal,
        setAchieveGoalId,
        reopenGoal,
        allGoals,
        taskGoalLinks,
        linkTaskToGoal,
        createLinkedTask,

        updateGoal,
        isSavingGoal,
        goalSaveFailed,
    } = useGoalCreation()
    const { data: tasks } = useGetTasksV4()
    const { data: review } = useGetGoalReview()
    const navigate = useNavigate()
    const isMobile = useIsMobile()
    const { data: recurringTemplates } = useRecurringTaskTemplates()
    const { data: recurringTemplateLinks = {} } = useGetGoalRecurringTemplateLinks()
    const { mutate: linkRecurringTemplateToGoals } = useLinkRecurringTemplateToGoals()
    const [isCreateOpen, setIsCreateOpen] = useState(false)
    const [isAttachOpen, setIsAttachOpen] = useState(false)
    const [isNoteOpen, setIsNoteOpen] = useState(false)
    const [isRedraftOpen, setIsRedraftOpen] = useState(false)
    const [unlinkedCreatedTaskLinks, setUnlinkedCreatedTaskLinks] = useState<TUnlinkedCreatedTaskLink[]>([])
    const [showDone, setShowDone] = useState(false)
    const [keptStepUpGoalIds, setKeptStepUpGoalIds] = useState<Set<string>>(() => new Set())
    const isReadOnly = useIsReadOnly()

    // Title and why save as you type, on the same hook and the same 1s debounce as notes and task
    // details. The hook's `body` slot is this pane's "why"; an emptied title becomes "(no title)"
    // rather than a blank one the server would reject.
    const { onEdit, syncIndicatorText } = useDebouncedEdit(
        {
            onSave: ({ id, title, body }) => updateGoal(id, { title, why: body }),
            isError: goalSaveFailed,
            isLoading: isSavingGoal,
        },
        DETAILS_SYNC_TIMEOUT
    )

    const linked = useMemo(() => linkedTasksForGoal(goal.id, tasks, taskGoalLinks), [goal.id, tasks, taskGoalLinks])
    const nextUp = useMemo(() => nextUpOrder(linked), [linked])
    const done = linked.filter((t) => t.is_done)
    const isArchived = isGoalArchived(goal)
    const isAchieved = isGoalAchieved(goal)
    // Archived or achieved: read-only, and nothing new can be attached. History is a separate rule —
    // an achieved goal keeps its reviews and contributions below; an archived one has none.
    const isClosed = isGoalClosed(goal)
    const isAccomplished = isAchieved || isLegacyFinishedGoal(goal)
    const isImmutable = isClosed || isReadOnly
    const unlinkedCreatedTasks = useMemo(
        () =>
            unlinkedCreatedTaskLinks
                .filter((link) => link.ownerGoalId === goal.id)
                .map((link) => ({ ...link, task: tasks?.find((task) => task.id === link.taskId) }))
                .filter(
                    (link): link is TUnlinkedCreatedTaskLink & { task: TTaskV4 } =>
                        !!link.task && !link.task.is_deleted && goalIdForTask(taskGoalLinks, link.task) !== link.goalId
                ),
        [goal.id, taskGoalLinks, tasks, unlinkedCreatedTaskLinks]
    )
    const linkedRecurringTemplates = useMemo(
        () =>
            activeRecurringTemplates(recurringTemplates).filter((template) =>
                (recurringTemplateLinks[template.id] ?? []).includes(goal.id)
            ),
        [goal.id, recurringTemplateLinks, recurringTemplates]
    )
    const recurringTemplateCandidates = useMemo(
        () =>
            activeRecurringTemplates(recurringTemplates).filter(
                (template) => !(recurringTemplateLinks[template.id] ?? []).includes(goal.id)
            ),
        [goal.id, recurringTemplateLinks, recurringTemplates]
    )
    const recurringGoalCountByTaskId = useMemo(() => {
        const counts: Record<string, number> = {}
        ;(tasks ?? []).forEach((task) => {
            if (!task.recurring_task_template_id) return
            counts[task.id] = recurringTemplateLinks[task.recurring_task_template_id]?.length ?? 0
        })
        return counts
    }, [recurringTemplateLinks, tasks])
    const recurringCreditGoalCount = linkedRecurringTemplates.reduce(
        (max, template) => Math.max(max, recurringTemplateLinks[template.id]?.length ?? 0),
        0
    )
    const candidates = useMemo(
        () => (tasks ?? []).filter((t) => !t.is_done && !t.is_deleted && !t.id_parent && !taskGoalLinks[t.id]),
        [tasks, taskGoalLinks]
    )
    const typeLabel = goal.goalType === 'time' ? 'Time-based' : 'Consistency'
    const isPaced = !!goal.weeklyTarget && goal.progressMode !== 'manual'
    const isPaused = goal.status === 'paused'
    const showStepUpOffer = !isImmutable && shouldOfferGoalStepUp(goal) && !keptStepUpGoalIds.has(goal.id)
    // Reviews are joined in from goal.reviews, never saved in `recent`: archiving discards `recent`
    // and the server will not write to it afterwards, so a review recorded there could not survive
    // the "Archive goal" answer that produced it.
    const recent = useMemo(() => {
        const reviewRows: TGoalContribution[] = isArchived
            ? []
            : (goal.reviews ?? []).map((entry) => ({
                  id: `review-${entry.weekStart ?? entry.at}`,
                  title: reviewRecentTitle(goal, entry),
                  source: REVIEW_SOURCE,
                  at: entry.at,
                  amount: 0,
              }))
        return [...goal.recent, ...reviewRows]
            .sort((a, b) => (b.at ?? '').localeCompare(a.at ?? ''))
            .slice(0, RECENT_LIMIT)
    }, [goal, isArchived])
    const readStrip = goalReadStrip(goal, DateTime.local())
    const stepTaskId = weekStepTaskId(goal, review)
    const unlink = (task: TTaskV4) => linkTaskToGoal(task.id, null, task.optimisticId)
    const dismissUnlinkedCreatedTask = (taskId: string) =>
        setUnlinkedCreatedTaskLinks((links) => links.filter((link) => link.taskId !== taskId))
    const addUnlinkedCreatedTask = (taskId: string, goalId: string) =>
        setUnlinkedCreatedTaskLinks((links) =>
            links.some((link) => link.taskId === taskId && link.goalId === goalId && link.ownerGoalId === goal.id)
                ? links
                : [...links, { taskId, goalId, ownerGoalId: goal.id }]
        )
    const retryUnlinkedCreatedTask = (task: TTaskV4, goalId: string) => {
        const targetGoal = allGoals.find((candidate) => candidate.id === goalId)
        if (targetGoal && isGoalClosed(targetGoal)) return
        linkTaskToGoal(task.id, goalId, task.optimisticId, {
            onSuccess: dismissUnlinkedCreatedTask,
            onError: (taskId) => addUnlinkedCreatedTask(taskId, goalId),
        })
    }

    const actionItems: GTMenuItem[] = [
        {
            label: 'Redraft plan',
            icon: icons.spinner,
            disabled: isPaused || isImmutable,
            tip: isArchived
                ? 'Restore this goal to redraft'
                : isAchieved
                ? 'Reopen this goal to redraft'
                : isPaused
                ? 'Resume this goal to redraft'
                : undefined,
            onClick: () => setIsRedraftOpen(true),
        },
        ...(isAchieved
            ? [
                  {
                      label: 'Reopen goal',
                      icon: icons.archiveRestore,
                      disabled: isReadOnly,
                      onClick: () => reopenGoal(goal.id),
                  },
              ]
            : []),
        isArchived
            ? {
                  label: 'Restore goal',
                  icon: icons.archiveRestore,
                  disabled: isReadOnly,
                  onClick: () => unarchiveGoal(goal.id),
              }
            : {
                  label: 'Archive goal',
                  icon: icons.archive,
                  disabled: isReadOnly,
                  onClick: () => setArchiveGoalId(goal.id),
              },
    ]
    // On a phone the details pane is its own route; send the user back to the list, where the
    // Accomplished section opens on the card that just moved.
    const openAchieve = () => setAchieveGoalId(goal.id, { onAchieved: () => isMobile && navigate('/goals') })
    const addRecurringHook = (template: TRecurringTaskTemplate) => {
        const goalIds = addGoalToTemplateHook(recurringTemplateLinks[template.id], goal.id)
        linkRecurringTemplateToGoals({ id: template.id, goalIds })
    }
    const removeRecurringHook = (template: TRecurringTaskTemplate) => {
        const goalIds = removeGoalFromTemplateHook(recurringTemplateLinks[template.id], goal.id)
        linkRecurringTemplateToGoals({ id: template.id, goalIds })
    }
    const dismissStepUpOffer = () => {
        setKeptStepUpGoalIds((current) => {
            const next = new Set(current)
            next.add(goal.id)
            return next
        })
    }
    const addStepUp = () => {
        dismissStepUpOffer()
        updateGoal(goal.id, goalStepUpPatch(goal))
    }

    return (
        <DetailsViewTemplate>
            <div className="flex flex-col gap-2">
                <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                        <GTTextField
                            key={goal.id}
                            type="plaintext"
                            value={goal.title}
                            onChange={(val) => onEdit({ id: goal.id, title: val })}
                            maxHeight={TITLE_MAX_HEIGHT}
                            fontSize="medium"
                            hideUnfocusedOutline
                            enterBehavior="blur"
                            aria-label="Goal title"
                            disabled={isImmutable}
                        />
                    </div>
                    <span className="shrink-0 pt-1.5 text-body-sm text-muted-foreground">{syncIndicatorText}</span>
                    <span className="flex shrink-0 items-center pt-1">
                        {isAccomplished ? (
                            <Tip content={accomplishedDateLabel(goal)} fitContent>
                                <span className="flex self-start">
                                    <GoalStatusBadge status={goal.status} isAccomplished />
                                </span>
                            </Tip>
                        ) : isArchived ? (
                            <Tip content={goal.archivedReason === 'done' ? 'Archived as done' : 'Archived'} fitContent>
                                <span className="flex self-start">
                                    <GoalStatusBadge
                                        status={goal.status}
                                        isArchived
                                        isFinished={goal.archivedReason === 'done'}
                                    />
                                </span>
                            </Tip>
                        ) : isReadOnly ? (
                            <GoalStatusBadge status={goal.status} />
                        ) : (
                            <StatusControl goal={goal} onChange={(patch) => updateGoal(goal.id, patch)} />
                        )}
                    </span>
                    <span className="shrink-0">
                        <GTDropdownMenu
                            hideCheckmark
                            align="end"
                            items={actionItems}
                            trigger={
                                <GTButton styleType="icon" icon={icons.ellipsisVertical} tooltipText="Goal actions" />
                            }
                        />
                    </span>
                </div>
                <div className="border-l-2 border-border py-0.5 pl-2">
                    <GTTextField
                        key={goal.id}
                        type="plaintext"
                        value={goal.why ?? ''}
                        placeholder="Why this matters"
                        onChange={(val) => onEdit({ id: goal.id, body: val })}
                        fontSize="small"
                        hideUnfocusedOutline
                        enterBehavior="blur"
                        aria-label="Why"
                        disabled={isImmutable}
                    />
                </div>
            </div>

            <div className="flex flex-col gap-2 py-1">
                <div className="flex min-w-0 flex-row flex-wrap items-center gap-2" data-testid="goal-properties">
                    <EditableChip
                        label="Timeframe"
                        icon={<Icon icon={icons.calendar_blank} color="gray" size="small" />}
                        value={goal.timeframeLabel}
                        onCommit={(next) => updateGoal(goal.id, { timeframeLabel: next })}
                        inputProps={{ placeholder: 'Next 90 days' }}
                        disabled={isImmutable}
                    >
                        {goal.timeframeLabel || 'Add a timeframe'}
                    </EditableChip>
                    <EndDateChip
                        endDate={goal.endDate}
                        onChange={(next) => updateGoal(goal.id, { endDate: next })}
                        disabled={isImmutable}
                    />
                    {goal.weeklyTarget ? (
                        <EditableChip
                            label="Weekly target"
                            icon={<Icon icon={icons.arrows_repeat} color="gray" size="small" />}
                            value={formatGoalAmount(goal.weeklyTarget)}
                            onCommit={(next) => {
                                const parsed = Number(next)
                                if (!Number.isFinite(parsed) || parsed <= 0 || parsed === goal.weeklyTarget) return
                                // Re-pacing forgives the weeks that were judged against the old target,
                                // so a goal never comes back from an edit already off track.
                                updateGoal(goal.id, { weeklyTarget: parsed, forgive: true })
                            }}
                            inputProps={{
                                type: 'number',
                                min: goal.unit === 'hrs' ? 0.5 : 1,
                                step: goal.unit === 'hrs' ? 0.5 : 1,
                            }}
                            disabled={isImmutable}
                        >
                            {formatGoalAmount(goal.weeklyTarget)}
                            {goal.unit === 'hrs' ? ' hrs' : '×'} / week
                        </EditableChip>
                    ) : (
                        <EditableChip
                            label="Target"
                            icon={<Icon icon={icons.check_circle_wavy} color="gray" size="small" />}
                            value={goal.targetLabel}
                            onCommit={(next) => updateGoal(goal.id, { targetLabel: next })}
                            disabled={isImmutable}
                        >
                            {goal.targetLabel || 'Add a target'}
                        </EditableChip>
                    )}
                    {!isImmutable && (
                        <Tip content={isPaused ? 'Resume goal' : 'Pause goal'} fitContent>
                            <button
                                type="button"
                                className={CHIP_BUTTON_CLASS}
                                onClick={() => updateGoal(goal.id, isPaused ? GOAL_RESUME_PATCH : GOAL_PAUSE_PATCH)}
                            >
                                <Icon icon={isPaused ? icons.play : icons.pause} color="gray" size="small" />
                                <span className={CHIP_VALUE_CLASS}>{isPaused ? 'Resume' : 'Pause'}</span>
                            </button>
                        </Tip>
                    )}
                    <PropertyChip label="Goal type" value={typeLabel}>
                        {typeLabel}
                    </PropertyChip>
                </div>
                {!isImmutable && (
                    <div className="flex w-full justify-end" data-testid="goal-lifecycle-actions">
                        <Button
                            size="sm"
                            className="min-h-touch w-full gap-1.5 bg-success-hover px-2.5 text-white hover:bg-success-text active:scale-[0.96] md:h-7 md:min-h-0 md:w-auto"
                            onClick={openAchieve}
                        >
                            <PartyPopper className="size-3.5" aria-hidden />
                            Goal achieved
                        </Button>
                    </div>
                )}
                {/* Un-achieving sits where the achieve button was. Reopening keeps all history and
                    can be achieved again, so it needs no confirm. */}
                {isAchieved && !isReadOnly && (
                    <div className="flex w-full justify-end" data-testid="goal-lifecycle-actions">
                        <Button
                            variant="outline"
                            size="sm"
                            className="min-h-touch w-full gap-1.5 px-2.5 active:scale-[0.96] md:h-7 md:min-h-0 md:w-auto"
                            onClick={() => reopenGoal(goal.id)}
                        >
                            <RotateCcw className="size-3.5" aria-hidden />
                            Reopen goal
                        </Button>
                    </div>
                )}
            </div>

            {/* The owner's own read from the weekly review. It sits beside the badge above and never
                edits it: when the two disagree, both stand. After two weeks it is context, not news. */}
            {readStrip &&
                (readStrip.isStale ? (
                    <p className="text-body-sm text-muted-foreground" data-testid="goal-read-strip">
                        Your read, {readStrip.date}: {readStrip.read}. {readStrip.detail}
                    </p>
                ) : (
                    <div
                        className="flex items-center gap-3 rounded-gt-md border border-border bg-card py-2 pl-3 pr-2"
                        data-testid="goal-read-strip"
                    >
                        <p className="min-w-0 text-body-sm text-secondary-foreground">
                            Your read, {readStrip.date}:{' '}
                            <span className="font-semibold text-foreground">{readStrip.read}</span>. {readStrip.detail}
                        </p>
                        {!isClosed && (
                            <Button
                                variant="outline"
                                size="sm"
                                className="ml-auto shrink-0 active:scale-[0.96]"
                                disabled={isReadOnly}
                                onClick={() => navigate(`/goals/review/${goal.id}`)}
                            >
                                Review
                            </Button>
                        )}
                    </div>
                ))}
            {!isClosed && goal.status === 'off_track' && (
                <div className="flex items-center gap-3 rounded-gt-md border border-border bg-card py-2 pl-3 pr-2 animate-in fade-in-0 slide-in-from-top-1">
                    <p className="min-w-0 text-body-sm text-secondary-foreground">
                        This goal is off track — decide how to get back on.
                    </p>
                    <Button
                        variant="outline"
                        size="sm"
                        className="ml-auto shrink-0 active:scale-[0.96]"
                        disabled={isReadOnly}
                        onClick={() => setRepairGoalId(goal.id)}
                    >
                        Repair
                    </Button>
                </div>
            )}
            {!isClosed && goal.status === 'paused' && (
                <div className="flex items-center gap-3 rounded-gt-md border border-border bg-card py-2 pl-3 pr-2">
                    <p className="min-w-0 text-body-sm text-secondary-foreground">
                        Paused — no penalty while it rests.
                    </p>
                    <Button
                        size="sm"
                        className="ml-auto shrink-0 active:scale-[0.96]"
                        disabled={isReadOnly}
                        onClick={() => updateGoal(goal.id, GOAL_RESUME_PATCH)}
                    >
                        Resume
                    </Button>
                </div>
            )}

            <div className="h-2" />

            {/* Next steps: the concrete work behind the plan, first because it is what you act on.
                Completing one saves a contribution. */}
            {/* A closed goal with nothing linked has no next steps to label. */}
            {!(isClosed && linked.length === 0) && (
                <div className="flex items-center gap-2 px-2 py-0.5">
                    <SectionLabel>Next steps</SectionLabel>
                    {done.length > 0 && (
                        <button
                            type="button"
                            aria-expanded={showDone}
                            onClick={() => setShowDone((v) => !v)}
                            className="text-label-md tabular-nums text-muted-foreground/70 hover:text-muted-foreground"
                        >
                            {done.length} done
                        </button>
                    )}
                </div>
            )}
            {!isClosed && nextUp.length === 0 && !(showDone && done.length > 0) && (
                <p className="rounded-gt-md border border-dashed border-border p-3 text-body-sm text-muted-foreground">
                    Nothing up next. Add a task to keep this goal moving.
                </p>
            )}
            {(nextUp.length > 0 || (showDone && done.length > 0)) && (
                <div className="flex shrink-0 flex-col overflow-hidden rounded-gt-md border border-border bg-background">
                    {nextUp.map((t) => (
                        <NextUpRow
                            key={t.id}
                            task={t}
                            onUnlink={() => unlink(t)}
                            readOnly={isImmutable}
                            isWeekStep={t.id === stepTaskId}
                        />
                    ))}
                    {showDone &&
                        done.map((t) => (
                            <NextUpRow key={t.id} task={t} onUnlink={() => unlink(t)} readOnly={isImmutable} />
                        ))}
                </div>
            )}
            {/* Same refusal as the attach row below: an archived goal takes no new links. Keyed by
                goal so a title handed back after a failed create never lands in another goal's field. */}
            {!isClosed && (
                <CreateNewItemInput
                    key={goal.id}
                    placeholder="Add a task"
                    submitLabel="Add task"
                    onSubmit={(title, restore) =>
                        createLinkedTask(title, goal.id, {
                            onCreateError: restore,
                            onLinkError: (taskId) => addUnlinkedCreatedTask(taskId, goal.id),
                        })
                    }
                />
            )}
            {unlinkedCreatedTasks.length > 0 && (
                <div className="flex flex-col gap-2">
                    {unlinkedCreatedTasks.map(({ task, goalId }) => {
                        const targetGoal = allGoals.find((candidate) => candidate.id === goalId)
                        const targetIsArchived = !!targetGoal && isGoalClosed(targetGoal)
                        return (
                            <div
                                key={task.id}
                                className="flex flex-wrap items-center gap-2 rounded-gt-md border border-border bg-card px-3 py-2 text-body-sm text-secondary-foreground"
                            >
                                <span className="min-w-0 flex-1 truncate">{task.title} created, but not linked.</span>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-7 shrink-0"
                                    disabled={targetIsArchived || isReadOnly}
                                    title={targetIsArchived ? 'Restore or reopen the goal to retry' : undefined}
                                    onClick={() => retryUnlinkedCreatedTask(task, goalId)}
                                >
                                    Retry
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 shrink-0"
                                    onClick={() => dismissUnlinkedCreatedTask(task.id)}
                                >
                                    Keep unlinked
                                </Button>
                            </div>
                        )
                    })}
                </div>
            )}
            {/* Nothing can be attached to an archived goal — the server refuses the link, so a
                composer here would leave a real unfiled task behind and only then fail. */}
            {!isImmutable && (
                <div className="flex items-center gap-1">
                    <AttachTaskPopover
                        open={isAttachOpen}
                        setOpen={setIsAttachOpen}
                        candidates={candidates}
                        onPick={(t) => linkTaskToGoal(t.id, goal.id, t.optimisticId)}
                    />
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 gap-1.5 px-2 text-muted-foreground"
                        onClick={() => setIsCreateOpen(true)}
                    >
                        <Icon icon={icons.plus} color="gray" size="small" />
                        New task
                    </Button>
                </div>
            )}

            <div className="h-2" />

            {isAccomplished ? (
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="text-body-sm font-semibold text-foreground">{accomplishedReadout(goal)}</span>
                    <span className="text-body-sm text-muted-foreground">{accomplishedDateLabel(goal)}</span>
                </div>
            ) : isPaced ? (
                <>
                    <ThisWeekBlock
                        goal={goal}
                        hasLinkedTasks={linked.length > 0}
                        isArchived={isImmutable}
                        onAttach={() => setIsAttachOpen(true)}
                        onNewTask={() => setIsCreateOpen(true)}
                        onRepair={() => setRepairGoalId(goal.id)}
                        repeatCount={linkedRecurringTemplates.length}
                        recurringCreditGoalCount={recurringCreditGoalCount}
                    />
                    {showStepUpOffer && (
                        <StepUpOffer
                            goal={goal}
                            onAdd={addStepUp}
                            onKeep={dismissStepUpOffer}
                            onDisable={() => updateGoal(goal.id, { rampStepUpDisabled: true })}
                        />
                    )}
                </>
            ) : (
                <div className="flex items-center gap-2">
                    <span className="text-body-sm font-semibold text-foreground">{goal.progressLabel}</span>
                    {/* No meter here, and that absence is the explanation. A self-graded verdict is
                        read on the badge above, so the badge is also the only place it is written —
                        coupling the grade to a log made every log a verdict. */}
                    {goal.progressMode === 'manual' || isReadOnly ? (
                        <span className="text-body-sm text-muted-foreground">Self-graded</span>
                    ) : (
                        <span className="ml-auto">
                            <UnpacedByHand goal={goal} />
                        </span>
                    )}
                </div>
            )}
            {goal.footnote && <p className="text-body-sm italic text-muted-foreground">{goal.footnote}</p>}

            <div className="h-2" />

            {!!goal.planCadences?.length && <PlanCadences cadences={goal.planCadences} />}

            {!!goal.planCadences?.length && <div className="h-2" />}

            {/* Repeats: the only way a goal repeats. A hooked template's cadence and the time it
                holds are what used to be claimed by advisory contributor labels the server could
                never credit. Archiving drops every hook and the server refuses new ones, so the
                section would be an empty picker that fails on use. */}
            {!isImmutable && (
                <Repeats
                    goal={goal}
                    templates={linkedRecurringTemplates}
                    candidates={recurringTemplateCandidates}
                    onAdd={addRecurringHook}
                    onRemove={removeRecurringHook}
                    onEditTarget={(patch) => updateGoal(goal.id, patch)}
                />
            )}

            <div className="h-2" />

            <div className="h-2" />
            <div className="flex items-center gap-2 px-2 py-0.5">
                <SectionLabel>Recent</SectionLabel>
                {/* Left of the label, like "N done" in Next up: the floating capture button owns
                    the bottom-right corner on a phone and would sit on top of it. */}
                {!isImmutable && !isNoteOpen && (
                    <button
                        type="button"
                        onClick={() => setIsNoteOpen(true)}
                        className="text-label-md text-muted-foreground/70 hover:text-muted-foreground"
                    >
                        + note
                    </button>
                )}
            </div>
            {!isImmutable && isNoteOpen && <NoteComposer goalId={goal.id} onDone={() => setIsNoteOpen(false)} />}
            <div className="flex flex-col">
                {recent.map((c, i) => (
                    <RecentRow
                        key={c.id ?? `${c.title}-${i}`}
                        contribution={c}
                        recurringGoalCount={c.taskId ? recurringGoalCountByTaskId[c.taskId] ?? 0 : 0}
                    />
                ))}
            </div>

            <GoalPlanDialogs
                goal={goal}
                isCreateOpen={isCreateOpen}
                onCreateOpenChange={setIsCreateOpen}
                isRedraftOpen={isRedraftOpen}
                onRedraftOpenChange={setIsRedraftOpen}
                onGoalLinkFailed={addUnlinkedCreatedTask}
            />
        </DetailsViewTemplate>
    )
}

export default GoalDetails
